/**
 * Bloc « C'est quoi, un vélo reconditionné MINT ? »
 *
 * Deux responsabilités indépendantes :
 *
 *  1. <recond-video> — lecture à la demande. Le <video> vit dans un <template> et
 *     n'est cloné qu'au clic : rien n'est téléchargé tant que le visiteur ne le
 *     demande pas, et la carte ne bouge pas (la boîte est déjà réservée en
 *     aspect-ratio côté CSS, donc CLS nul). Seul le <video> est extrait du
 *     fragment cloné et inséré (même précaution que `video-lazy`, cf.
 *     sections/media-banner.liquid:286-294) : `is-playing` (qui masque
 *     poster/dégradé/pastille/bouton/légende, cf. CSS) n'est posé qu'à l'event
 *     `playing` — jamais avant, jamais si le clonage ne renvoie aucun <video>.
 *     Si la lecture échoue ensuite (`error` du <video> ou rejet de `play()`),
 *     `is-playing` est retiré (s'il avait été posé) ET le <video> est de toute
 *     façon désinséré : en `position: absolute; inset: 0` dans la même boîte
 *     que le poster (cf. CSS), un lecteur cassé resterait peint par-dessus
 *     poster et bouton même une fois `is-playing` levé — ne retirer que la
 *     classe ne les rend pas visibles tant que ce qui les recouvre reste là.
 *     Sans cette garde, un média qui ne se rend pas (source supprimée,
 *     transcodage en cours) donnait une carte noire irréversible : template
 *     détruit, bouton masqué, plus rien à recliquer.
 *     Le focus clavier suit l'activation : une fois le <video> inséré, il
 *     reçoit le focus (il est `controls`, donc nativement focusable ET
 *     tabulable — pas de `tabindex` à poser, ça casserait le Shift+Tab vers
 *     ses propres contrôles) pour ne pas le laisser retomber sur <body> quand
 *     le bouton qui le portait disparaît. `{ preventScroll: true }` : le CSS
 *     place le <video> dans la même boîte que le poster (racine du problème
 *     corrigée — avant, en flux normal, il débordait sous `overflow: hidden`),
 *     mais le focus reste posé sans laisser Chrome décider d'un scroll —
 *     `scroll-behavior: smooth` (`mint.css`) l'animerait, même piège que
 *     l'anti-pattern n°31. Sur l'échec, `restorePoster` rend le focus au
 *     bouton UNIQUEMENT si c'est le <video> désinséré qui le portait
 *     (idempotent : un visiteur déjà reparti focaliser autre chose n'est pas
 *     repris de force).
 *
 *  2. Tracking — `recond_view` quand le bloc entre dans le viewport à 40 %,
 *     `click_cta` « video_reconditionnement » au clic sur le CTA (avant même de
 *     savoir si la vidéo se rendra — un visiteur qui clique sur une vidéo cassée
 *     laisse une trace), `video_start` au premier `playing` réel, `click_cta`
 *     « video_reconditionnement_erreur » si le <video> lève une erreur (une seule
 *     fois, `{ once: true }`). Comparer le clic et `video_start` donne le taux
 *     d'échec de lecture —
 *     ce que fausserait un second clic avant que `playing` ne tranche (rien
 *     ne bouge à l'écran entre les deux, donc rien n'empêche l'utilisateur de
 *     recliquer) : `dataset.recondStarted` verrouille le handler dès le
 *     premier clic, un rappel ultérieur ne repousse rien et ne relance pas
 *     `start()`.
 *
 * Le verrou de `recond_view` est posé PAR INSTANCE (data-recond-view-sent) et non
 * sur window, contrairement à product-inspection.js : deux instances du composant
 * sur une même page doivent pouvoir se compter toutes les deux.
 *
 * Le `<script src="…reconditionnement-mint.js" defer>` est émis PAR LE SNIPPET :
 * deux instances du composant sur une page = deux balises <script> avec le même
 * src, et le navigateur exécute CHAQUE balise (il ne dédoublonne que le
 * téléchargement, pas l'exécution) → cette IIFE tourne deux fois. Sans garde, les
 * deux exécutions appellent `init(document)` qui interroge TOUT le document et
 * posent donc chacune un IntersectionObserver sur le MÊME root, avant qu'aucun des
 * deux callbacks n'ait couru — un verrou lu seulement à la création de l'observer
 * (comme le ferait `if (root.dataset.recondViewSent === 'true') return;` posé une
 * seule fois dans `observeView`) ne voit encore rien et laisse les deux passer.
 * Double parade :
 *  (a) le verrou est RELU dans le callback de l'IntersectionObserver, juste avant
 *      le `pushEvent` — JS étant mono-thread, le callback qui s'exécute en premier
 *      pose le verrou avant que le second ne s'exécute à son tour, donc le second
 *      le trouve déjà posé et sort sans rien envoyer ;
 *  (b) le fichier entier ne s'exécute qu'une fois (garde `window.__recondMintInit`
 *      en tête d'IIFE) : la seconde balise <script> ne fait plus rien du tout, donc
 *      dans la pratique un seul IntersectionObserver est même créé par root. Cette
 *      garde ne bloque QUE les définitions/écouteurs globaux (posés une fois) : le
 *      handler `shopify:section:load` enregistré par la première exécution reste
 *      actif et continue d'appeler `init()` sur le DOM injecté par l'éditeur.
 *
 * Le custom element est gardé par customElements.get() : le fichier est chargé par
 * le snippet, donc une fois par instance rendue, et un second define() lèverait
 * une NotSupportedError (c'est exactement le bug latent de video-lazy, défini deux
 * fois dans media-banner.liquid:359 et media-with-text-new.liquid:433).
 */
(function () {
  'use strict';

  if (window.__recondMintInit) return;
  window.__recondMintInit = true;

  function pushEvent(name, data) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, data));
  }

  if (!customElements.get('recond-video')) {
    class RecondVideo extends HTMLElement {
      connectedCallback() {
        if (this.dataset.recondBound === 'true') return;
        this.dataset.recondBound = 'true';

        var button = this.querySelector('[data-recond-play]');
        if (!button) return;
        button.addEventListener('click', this.handlePlayClick.bind(this));
      }

      handlePlayClick() {
        if (this.dataset.recondStarted === 'true') return;
        this.dataset.recondStarted = 'true';

        pushEvent('click_cta', {
          cta_name: 'video_reconditionnement',
          cta_location: this.dataset.sectionLocation || '',
        });
        this.start();
      }

      start() {
        var template = this.querySelector('[data-recond-video-template]');
        if (!template) return;

        // Le fragment n'est jamais inséré tel quel : on n'y prend que le <video>,
        // et on ne touche à rien (template compris) tant qu'il n'y en a pas un.
        var video = template.content.cloneNode(true).querySelector('video');
        if (!video) return;

        template.remove();
        this.appendChild(video);

        video.setAttribute('playsinline', '');
        video.focus({ preventScroll: true });

        var self = this;

        video.addEventListener(
          'playing',
          function () {
            self.classList.add('is-playing');
            pushEvent('video_start', {
              video_title: self.dataset.videoTitle || '',
              video_provider: 'shopify',
              video_duration: isFinite(video.duration) ? Math.round(video.duration) : 0,
              section_location: self.dataset.sectionLocation || '',
            });
          },
          { once: true },
        );

        video.addEventListener(
          'error',
          function () {
            self.restorePoster(video);
            // Toujours consécutive au clic sur « Regarder » (la vidéo n'est créée qu'à ce
            // moment) : portée par `click_cta`, seul event relayé par GTM ici.
            pushEvent('click_cta', {
              cta_name: 'video_reconditionnement_erreur',
              cta_location: self.dataset.sectionLocation || '',
            });
          },
          { once: true },
        );

        var played = video.play();
        if (played && typeof played.catch === 'function') {
          played.catch(function () {
            self.restorePoster(video);
          });
        }
      }

      restorePoster(video) {
        var hadFocus = this.contains(document.activeElement);
        this.classList.remove('is-playing');
        if (video.parentNode === this) this.removeChild(video);
        if (!hadFocus) return;
        var button = this.querySelector('[data-recond-play]');
        if (button) button.focus({ preventScroll: true });
      }
    }

    customElements.define('recond-video', RecondVideo);
  }

  function observeView(root) {
    if (root.dataset.recondViewSent === 'true') return;
    if (typeof window.IntersectionObserver !== 'function') return;

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting !== true) continue;

          observer.disconnect();

          if (root.dataset.recondViewSent === 'true') return;
          root.dataset.recondViewSent = 'true';

          pushEvent('recond_view', {
            section_location: root.dataset.sectionLocation || '',
            has_video: root.dataset.hasVideo === 'true',
          });
          return;
        }
      },
      { threshold: 0.4 },
    );

    observer.observe(root);
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-recond-mint]');
    for (var i = 0; i < roots.length; i++) {
      observeView(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
